import { useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import type { buildThemeClasses } from "../theme";
import {
  qualifyLeadHuman,
  type Lead,
  type QualificationGrade,
} from "../services/leadsService";
import { trackLeadQualified } from "../../../analytics/crmAnalytics";

// ============================================================================
// LeadQualifications — shows a lead's TWO grades side by side:
//   • IA      — written automatically after a call by the LLM (read-only).
//   • Agente  — set here by the assigned human agent (editable).
// Lets the team compare the machine read against the rep's own judgment.
// ============================================================================

type TC = ReturnType<typeof buildThemeClasses>;

const GRADES: QualificationGrade[] = ["A", "B", "C", "D", "E"];

const GRADE_TONE: Record<string, string> = {
  A: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  B: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  C: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  D: "bg-orange-500/15 text-orange-600",
  E: "bg-red-500/15 text-red-500",
};

function GradeBadge({ grade }: { grade: string | null | undefined }) {
  if (!grade) return <span className="text-sm text-gray-400">—</span>;
  return (
    <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg text-sm font-bold ${GRADE_TONE[grade] ?? "bg-gray-400/15 text-gray-500"}`}>
      {grade}
    </span>
  );
}

function SuccessTag({ success, label }: { success: boolean | null | undefined; label?: string | null }) {
  if (success == null) return null;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
        success ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-red-500/15 text-red-500"
      }`}
    >
      {label || (success ? "éxito" : "fracaso")}
    </span>
  );
}

export default function LeadQualifications({
  lead,
  tc,
  onQualified,
}: {
  lead: Lead;
  tc: TC;
  onQualified: (lead: Lead) => void;
}) {
  const ai = lead.aiQualification ?? null;
  const human = lead.humanQualification ?? null;

  const [grade, setGrade] = useState<QualificationGrade | "">((human?.grade as QualificationGrade) ?? "");
  const [score, setScore] = useState<string>(human?.score != null ? String(human.score) : "");
  const [success, setSuccess] = useState<"" | "yes" | "no">(
    human?.success == null ? "" : human.success ? "yes" : "no",
  );
  const [notes, setNotes] = useState<string>(human?.notes ?? "");
  const [saving, setSaving] = useState(false);

  const inputClass = `w-full rounded-lg border px-2.5 py-1.5 text-sm outline-none border-line bg-app text-content`;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await qualifyLeadHuman(lead.id, {
        grade: grade || null,
        score: score === "" ? null : Number(score),
        success: success === "" ? null : success === "yes",
        notes: notes.trim() || null,
      });
      // `hadAiQualification` mide si el agente de IA sirve de algo: si el
      // humano siempre recalifica, la calificacion automatica no se usa. Las
      // notas del vendedor son texto libre y solo viaja si las hubo.
      trackLeadQualified({
        grade: grade || "none",
        score: score === "" ? 0 : Number(score),
        hasNotes: notes.trim().length > 0,
        hadAiQualification: Boolean(lead.aiQualification),
      });
      onQualified(updated);
      Swal.fire({ icon: "success", title: "Calificación guardada", timer: 1500, showConfirmButton: false });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo guardar",
        text: err.response?.data?.message || err.message || "Error al calificar",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`}>
      <p className={`mb-2 text-xs font-semibold uppercase tracking-wide ${tc.textSecondary}`}>Calificación</p>

      <div className="grid grid-cols-2 gap-3">
        {/* AI grade (read-only) */}
        <div className={`rounded-lg border p-2.5 ${tc.cardBorder}`}>
          <div className="mb-1 flex items-center justify-between">
            <span className={`flex items-center gap-1 text-[11px] font-medium ${tc.textSecondary}`}>
              <i className="ri-robot-2-line text-primary-500" /> IA
            </span>
            <SuccessTag success={ai?.success} label={ai?.outcomeLabel} />
          </div>
          <div className="flex items-center gap-2">
            <GradeBadge grade={ai?.grade} />
            <span className={`text-sm ${tc.text}`}>{ai?.score != null ? `${ai.score}/100` : "Sin calificar"}</span>
          </div>
          {ai?.summary && <p className={`mt-2 text-[11px] leading-snug ${tc.textSecondary}`}>{ai.summary}</p>}
          {ai?.callerType && (
            <p className="mt-1 text-[10px] text-gray-400">
              vía {ai.callerType === "ai" ? "llamada IA" : "llamada humana"}
            </p>
          )}
        </div>

        {/* Human grade (editable) */}
        <div className={`rounded-lg border p-2.5 ${tc.cardBorder}`}>
          <div className="mb-1 flex items-center justify-between">
            <span className={`flex items-center gap-1 text-[11px] font-medium ${tc.textSecondary}`}>
              <i className="ri-user-line text-primary-500" /> Agente
            </span>
            <SuccessTag success={human?.success} />
          </div>

          <div className="space-y-2">
            <div className="flex gap-1">
              {GRADES.map((g) => (
                <button
                  key={g}
                  onClick={() => setGrade((cur) => (cur === g ? "" : g))}
                  className={`h-7 w-7 rounded-md text-xs font-bold transition ${
                    grade === g
                      ? GRADE_TONE[g]
                      : "bg-subtle text-content-muted hover:bg-line"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={100}
                placeholder="Score"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                className={`${inputClass} w-20`}
              />
              <select value={success} onChange={(e) => setSuccess(e.target.value as "" | "yes" | "no")} className={inputClass}>
                <option value="">Resultado…</option>
                <option value="yes">Éxito</option>
                <option value="no">Fracaso</option>
              </select>
            </div>
            <textarea
              rows={2}
              placeholder="Notas del agente…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputClass}
            />
            <button
              onClick={save}
              disabled={saving}
              className="w-full rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-on-primary hover:bg-primary-400 disabled:opacity-50"
            >
              {saving ? <i className="ri-loader-4-line animate-spin" /> : "Guardar calificación"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
